import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useCallback, useEffect, useRef } from 'react';

import { useDonationFormBuilder } from '../useDonationFormBuilder';
import { getFormsPreviewUrl } from './RightPanel.utils';
import RightPanelTabs from './RightPanelTabs';

import * as styles from './RightPanel.css';

function RightPanel(): JSX.Element {
  const { amounts, monthlyAmounts, donationFrequency, allowMonthlyDonations, allowDescriptions } =
    useDonationFormBuilder();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const { url: previewUrl, origin: previewOrigin } = getFormsPreviewUrl();

  // Determine which amounts to use based on frequency
  const currentAmounts =
    allowMonthlyDonations && donationFrequency === 'monthly' ? monthlyAmounts : amounts;

  // Use JSON string for stable dependency comparison in useEffect
  // Only include descriptions if allowDescriptions is true
  const amountsData = currentAmounts.map((amount) => ({
    key: amount.id,
    value: amount.value,
    description: allowDescriptions ? amount.description : '',
  }));

  const amountsDataJson = JSON.stringify(amountsData);

  // Use a ref to always have the latest amountsData without recreating the callback
  const amountsDataRef = useRef(amountsData);
  useEffect(() => {
    amountsDataRef.current = amountsData;
  }, [amountsData]);

  // Store frequency in ref for postMessage
  const frequencyRef = useRef(donationFrequency);
  useEffect(() => {
    frequencyRef.current = donationFrequency;
  }, [donationFrequency]);

  // Store allowDescriptions in ref for postMessage
  const allowDescriptionsRef = useRef(allowDescriptions);
  useEffect(() => {
    allowDescriptionsRef.current = allowDescriptions;
  }, [allowDescriptions]);

  const sendToPreview = useCallback(() => {
    if (!iframeRef.current?.contentWindow) {
      return;
    }

    // Send postMessage with the current amountsData, frequency, and allowDescriptions
    iframeRef.current.contentWindow.postMessage(
      {
        type: 'feathr:preview:options',
        options: amountsDataRef.current,
        frequency: frequencyRef.current,
        allowDescriptions: allowDescriptionsRef.current,
      },
      previewOrigin,
    );
  }, [previewOrigin]);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) {
      return;
    }

    // Set up the iframe with the preview URL (no query params)
    iframe.src = previewUrl;
    iframe.allow = 'payment *';
    iframe.setAttribute('allowpaymentrequest', 'true');
    iframe.setAttribute('scrolling', 'no');
    iframe.setAttribute('frameborder', '0');

    // Send options to iframe when loaded
    const handleLoad = () => {
      sendToPreview();
    };

    iframe.addEventListener('load', handleLoad);

    return () => {
      iframe.removeEventListener('load', handleLoad);
    };
  }, []);

  // Send options whenever amounts, frequency, or allowDescriptions change
  useEffect(() => {
    sendToPreview();
  }, [amountsDataJson, donationFrequency, allowDescriptions, sendToPreview]);

  return (
    <div className={styles.root}>
      <RightPanelTabs.Beta />
      <div className={styles.content}>
        <iframe className={styles.iframe} ref={iframeRef} title={'Forms Embed'} />
      </div>
    </div>
  );
}

export default observer(RightPanel);
